import React, {useState, useMemo, useEffect} from 'react';
import analytics from '@react-native-firebase/analytics';
import {
  Alert,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
  TouchableOpacity,
  FlatList,
} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {RFValue} from 'react-native-responsive-fontsize';

import {Header} from '../../../components/Interface/Headers/Header';
import {Titles} from '../../../components/Interface/Typographies/Titles';
import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {Input} from '../../../components/Interface/Forms/Inputs/Input';
import {Picker} from '../../../components/Interface/Forms/Inputs/Picker';
import {Loading} from '../../../components/Interface/Loadings/Loading';
import LegacyBottomSheet from '../../../components/Interface/LegacyBottomSheet';

import {salvarDomicilioCredito} from '../../../services/creditoMova';
import {api} from '../../../services/api';

import {
  Container,
  Content,
  Footer,
  Form,
  InputGroup,
  InputGrid,
  NoticeCard,
  NoticeIcon,
  NoticeText,
  BankInputContainer,
  BankDropdownIcon,
  BankSelectorLabel,
  BankSelectorValue,
  BankSheetTitle,
  BankSheetItem,
  BankSheetItemText,
  BankSheetEmpty,
  SearchForm,
  DigitoLabel,
  DigitoInputField,
} from './styles';
import {EmprestimoEligibilityGate} from '../../../components/shared/EmprestimoEligibilityGate';
import {SearchInput} from '../../../components/Interface/Forms/Inputs/SearchInput';

interface RouteParams {
  simulacao: any;
  installments_option_id: string;
  selectedOption: any;
  endereco_id: string;
}

type BankOption = {label: string; value: string};

const tiposChavePix = [
  {label: 'CPF', value: 'cpf'},
  {label: 'CNPJ', value: 'cnpj'},
  {label: 'E-mail', value: 'email'},
  {label: 'Telefone', value: 'telefone'},
  {label: 'Chave aleatória', value: 'chave_aleatoria'},
];

const tiposConta = [
  {label: 'Conta Corrente', value: 'corrente'},
  {label: 'Conta Poupança', value: 'poupanca'},
];

const pixMaskConfig: Record<string, {mask: string; placeholder: string}> = {
  cpf: {mask: '[000].[000].[000]-[00]', placeholder: '000.000.000-00'},
  cnpj: {
    mask: '[00].[000].[000]/[0000]-[00]',
    placeholder: '00.000.000/0000-00',
  },
  telefone: {
    mask: '+55 ([00]) [00000]-[0000]',
    placeholder: '+55 (00) 00000-0000',
  },
};

const normalizeBankCode = (code: unknown) => {
  if (code === null || code === undefined) {
    return '';
  }
  const codeStr = String(code).trim();
  if (/^\d{1,2}$/.test(codeStr)) {
    return codeStr.padStart(3, '0');
  }
  return codeStr;
};

const buildBankOptions = (data: unknown): BankOption[] => {
  const toTitleCase = (value: string) =>
    value
      .toLowerCase()
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  if (Array.isArray(data)) {
    return data
      .map((item: any) => {
        const code = normalizeBankCode(
          item.codigo ?? item.code ?? item.numero ?? item.value ?? item.id,
        );
        const rawName = String(
          item.nome ?? item.name ?? item.banco ?? item.label ?? '',
        ).trim();
        const name = toTitleCase(rawName);
        const label = code && name ? `${code} ${name}` : name || code;
        return label ? {label, value: code || label} : null;
      })
      .filter((item: BankOption | null): item is BankOption => Boolean(item));
  }

  if (data && typeof data === 'object') {
    return Object.entries(data as Record<string, unknown>)
      .map(([code, name]) => {
        const codeStr = normalizeBankCode(code);
        const nameStr = toTitleCase(String(name ?? '').trim());
        const label =
          codeStr && nameStr ? `${codeStr} ${nameStr}` : nameStr || codeStr;
        return label ? {label, value: codeStr || label} : null;
      })
      .filter((item: BankOption | null): item is BankOption => Boolean(item));
  }

  return [];
};

export function EmprestimoDomicilio() {
  const navigation = useNavigation();
  const route = useRoute();
  const params = route.params as RouteParams;
  const [loading, setLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState<BankOption[]>([]);

  // PIX fields
  const [tipoChavePix, setTipoChavePix] = useState('select');
  const [chavePix, setChavePix] = useState('');
  const [chavePixRaw, setChavePixRaw] = useState('');

  const hasMask = ['cpf', 'cnpj', 'telefone'].includes(tipoChavePix);

  const handleTipoChavePixChange = (value: string) => {
    setTipoChavePix(value);
    setChavePix('');
    setChavePixRaw('');
  };

  const handleChavePixChange = (_formatted: string, extracted?: string) => {
    if (hasMask) {
      setChavePix(_formatted);
      setChavePixRaw(extracted || '');
    } else {
      setChavePix(_formatted);
      setChavePixRaw(_formatted);
    }
  };

  // Bank fields
  const [codigoBanco, setCodigoBanco] = useState('');
  const [bankSheetOpen, setBankSheetOpen] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [agencia, setAgencia] = useState('');
  const [digitoAgencia, setDigitoAgencia] = useState('');
  const [conta, setConta] = useState('');
  const [digitoConta, setDigitoConta] = useState('');
  const [tipoConta, setTipoConta] = useState('select');

  useEffect(() => {
    let isMounted = true;

    const loadBanks = async () => {
      try {
        const response = await api.get('/get_bancos');
        const options = buildBankOptions(response.data)
          .slice()
          .sort((a, b) => {
            const getName = (label: string) =>
              label.replace(/^\d+\s+/, '').trim();
            const nameA = getName(a.label);
            const nameB = getName(b.label);
            const byName = nameA.localeCompare(nameB, 'pt-BR');
            return byName !== 0
              ? byName
              : a.label.localeCompare(b.label, 'pt-BR');
          });
        if (isMounted) {
          setBankOptions(options);
        }
      } catch {
        Alert.alert(
          'Ocorreu um problema',
          'Não foi possível listar os bancos disponíveis, tente novamente mais tarde.',
        );
      }
    };

useEffect(() => {
  analytics().logEvent('emprestimo_tela_domicilio');
}, []);



    loadBanks();
    return () => {
      isMounted = false;
    };
  }, []);

  const formEnabled = useMemo(
    () =>
      tipoChavePix !== 'select' &&
      chavePixRaw.trim().length > 0 &&
      codigoBanco.trim().length > 0 &&
      agencia.trim().length > 0 &&
      conta.trim().length > 0 &&
      digitoConta.trim().length > 0 &&
      tipoConta !== 'select',
    [
      tipoChavePix,
      chavePixRaw,
      codigoBanco,
      agencia,
      conta,
      digitoConta,
      tipoConta,
    ],
  );

  const handleContinue = async () => {
  analytics().logEvent('emprestimo_clicou_continuar_domicilio');
  try {
    setLoading(true);

    const domicilioResponse = await salvarDomicilioCredito({
        tipo_transferencia: 'pix',
        tipo_chave_pix: tipoChavePix as
          | 'cpf'
          | 'cnpj'
          | 'email'
          | 'telefone'
          | 'chave_aleatoria',
        chave_pix: chavePixRaw.trim(),
        codigo_banco: codigoBanco.trim(),
        agencia: agencia.trim(),
        digito_agencia: digitoAgencia.trim() || undefined,
        conta: conta.trim(),
        digito_conta: digitoConta.trim(),
        tipo_conta: tipoConta as 'corrente' | 'poupanca',
      });

      (navigation as any).navigate('EmprestimoDocumentosIntro', {
        ...params,
        domicilio_id: domicilioResponse.id,
      });
    } catch (error: any) {
      const body = error?.response?.data;
      const errorMessage: string | undefined =
        body?.error || body?.msg || body?.message || undefined;
      navigation.navigate('ConfirmationScreen', {
        title: 'Ocorreu um problema',

        text:
          errorMessage ||
          'Ocorreu um erro ao prosseguir com sua solicitação. Por favor, tente novamente mais tarde.',
        buttonAction: () => navigation.goBack(),
        closeIconAction: () => navigation.navigate('Initial'),
        buttonText: 'Tentar novamente',
        type: 'error',
        showSupport: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredBanks = useMemo(
    () =>
      bankSearch.trim()
        ? bankOptions.filter(b =>
            b.label.toLowerCase().includes(bankSearch.toLowerCase()),
          )
        : bankOptions,
    [bankOptions, bankSearch],
  );

  const closeBankSheet = () => {
    setBankSheetOpen(false);
    setBankSearch('');
  };

  return (
    <>
      <LegacyBottomSheet isOpen={bankSheetOpen} onClose={closeBankSheet}>
        <BankSheetTitle>Selecione um banco</BankSheetTitle>
        <SearchForm>
          <SearchInput
            placeholder="Buscar por código ou nome..."
            value={bankSearch}
            onChangeText={setBankSearch}
            autoFocus
            returnKeyType="search"
          />
        </SearchForm>
        <FlatList
          data={filteredBanks}
          keyExtractor={item => item.value}
          style={{maxHeight: RFValue(250)}}
          keyboardShouldPersistTaps="handled"
          renderItem={({item}) => (
            <BankSheetItem
              onPress={() => {
                setCodigoBanco(item.value);
                closeBankSheet();
              }}>
              <BankSheetItemText>{item.label}</BankSheetItemText>
            </BankSheetItem>
          )}
          ListEmptyComponent={
            <BankSheetEmpty>Nenhum banco encontrado</BankSheetEmpty>
          }
        />
      </LegacyBottomSheet>
      <EmprestimoEligibilityGate />
      {loading && <Loading />}
      <SafeAreaView edges={['bottom']} style={{flex: 1}}>
        <Header icon="chevron-left" />

        <KeyboardAvoidingView
          style={{flex: 1}}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{
              paddingBottom: Platform.OS === 'ios' ? RFValue(20) : RFValue(40),
            }}>
            <Container>
              <Content>
                <Titles
                  title="Dados bancários"
                  subtitle="Informe os dados para recebimento do empréstimo."
                />

                <Form>
                  <NoticeCard>
                    <NoticeIcon name="alert-circle" />
                    <NoticeText>
                      A conta informada deve estar vinculada ao seu CPF, o mesmo
                      cadastrado no app Baratão. Contas de terceiros não serão
                      aceitas.
                    </NoticeText>
                  </NoticeCard>

                  <InputGroup>
                    <Picker
                      label="Tipo da chave PIX"
                      value={tipoChavePix}
                      placeholder={{label: 'Selecione', value: 'select'}}
                      onChangeValue={handleTipoChavePixChange}
                      items={tiposChavePix}
                    />
                  </InputGroup>

                  <InputGroup>
                    <Input
                      key={tipoChavePix}
                      labelText="Chave PIX"
                      placeholder={
                        hasMask
                          ? pixMaskConfig[tipoChavePix].placeholder
                          : tipoChavePix === 'email'
                          ? 'email@exemplo.com'
                          : 'Informe sua chave PIX'
                      }
                      value={chavePix}
                      onChangeText={handleChavePixChange}
                      {...(hasMask
                        ? {mask: pixMaskConfig[tipoChavePix].mask}
                        : {})}
                      keyboardType={
                        hasMask
                          ? 'number-pad'
                          : tipoChavePix === 'email'
                          ? 'email-address'
                          : 'default'
                      }
                      autoCapitalize="none"
                    />
                  </InputGroup>

                  <InputGroup>
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => setBankSheetOpen(true)}>
                      <BankSelectorLabel>Banco</BankSelectorLabel>
                      <BankInputContainer>
                        <BankSelectorValue isPlaceholder={!codigoBanco}>
                          {codigoBanco
                            ? bankOptions.find(b => b.value === codigoBanco)
                                ?.label || codigoBanco
                            : 'Selecione um banco'}
                        </BankSelectorValue>
                        <BankDropdownIcon name="chevron-down" />
                      </BankInputContainer>
                    </TouchableOpacity>
                  </InputGroup>

                  <InputGrid>
                    <InputGroup style={{flex: 2}}>
                      <Input
                        labelText="Agência"
                        placeholder="Agência"
                        value={agencia}
                        onChangeText={setAgencia}
                        keyboardType="number-pad"
                        maxLength={6}
                      />
                    </InputGroup>
                    <InputGroup style={{flex: 1}}>
                      <DigitoLabel>Dígito</DigitoLabel>
                      <DigitoInputField
                        placeholder="Dígito"
                        placeholderTextColor="#999"
                          keyboardType="number-pad"
                        autoCapitalize="characters"
                        value={digitoAgencia}
                        onChangeText={text =>
                          setDigitoAgencia(
                            text.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
                          )
                        }
                        maxLength={2}
                      />
                    </InputGroup>
                  </InputGrid>

                  <InputGrid>
                    <InputGroup style={{flex: 2}}>
                      <Input
                        labelText="Conta"
                        placeholder="Conta"
                        value={conta}
                        onChangeText={setConta}
                        keyboardType="number-pad"
                        maxLength={12}
                      />
                    </InputGroup>
                    <InputGroup style={{flex: 1}}>
                      <DigitoLabel>Dígito</DigitoLabel>
                      <DigitoInputField
                        placeholder="Dígito"
                        placeholderTextColor="#999"
                        keyboardType="number-pad"
                        autoCapitalize="characters"
                        value={digitoConta}
                        onChangeText={text =>
                          setDigitoConta(
                            text.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
                          )
                        }
                        maxLength={1}
                      />
                    </InputGroup>
                  </InputGrid>

                  <InputGroup>
                    <Picker
                      label="Tipo de conta"
                      value={tipoConta}
                      placeholder={{label: 'Selecione', value: 'select'}}
                      onChangeValue={setTipoConta}
                      items={tiposConta}
                    />
                  </InputGroup>
                </Form>
              </Content>

              <Footer>
                <Button
                  title={loading ? 'Salvando...' : 'Continuar'}
                  onPress={handleContinue}
                  enabled={formEnabled && !loading}
                />
              </Footer>
            </Container>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
}
